import React from "react";
import { FiTool } from "react-icons/fi";

import { useEquipmentPage } from '../context/EquipmentPageContext';
import { daysUntil, formatDate, localDate, monthRange, relativeDue, retired } from '../equipmentData';

export default function ComplianceAndMaintenance() {
  const { records, summary, loading, error, today, setRange, filterInventory, setCalendarOpen } = useEquipmentPage();
  const percentage = number => loading || error ? 0 : summary.percentage(number);
  const value = number => loading ? '…' : error ? '—' : number;
  const maintenanceItems = records.filter(item => !retired(item) && item.requiresRoutineMaintenance && daysUntil(item.nextMaintenanceDate, today) !== null && daysUntil(item.nextMaintenanceDate, today) >= 0)
    .sort((a, b) => a.nextMaintenanceDate.localeCompare(b.nextMaintenanceDate)).map(item => ({ ...item,
      date: formatDate(item.nextMaintenanceDate), due: relativeDue(daysUntil(item.nextMaintenanceDate, today)), colorClass: 'text-orange-500 font-medium',
    }));

  return (
    <div className="w-full max-w-md flex flex-col gap-5 font-sans text-slate-900">
      {/* SECTION 1: Compliance Overview Panel */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        {/* Card Header Toolbar Container */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <h3 title="Register checks based on operational status, condition, PAT results and maintenance dates. Failed or overdue requirements need action; missing or soon-due requirements need attention." className="text-base font-bold text-slate-900">
            Compliance Overview
          </h3>

          <select aria-label="Equipment compliance period" value="range" onChange={event => {
            if (event.target.value === 'month') setRange(monthRange());
            if (event.target.value === 'calendar') { const now = new Date(); setRange({ startDate: localDate(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: localDate(now) }); }
          }} className="text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-500 cursor-pointer shadow-sm">
            <option value="range">Selected Range</option>
            <option value="month">Last Month</option>
            <option value="calendar">This Month</option>
          </select>
        </div>

        {/* Content Layout Inner Container Grid */}
        <div className="grid grid-cols-12 gap-2 items-center">
          {/* Vector Donut Graphical Layout Chart Frame */}
          <div className="col-span-5 flex justify-center relative py-1">
            <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 36 36">
              {/* Underlay Missing Track */}
              <circle
                cx="18"
                cy="18"
                r="15.915"
                fill="transparent"
                stroke="#f1f5f9"
                strokeWidth="3.2"
              />

              {/* Compliant Section */}
              <circle
                cx="18"
                cy="18"
                r="15.915"
                fill="transparent"
                stroke="#22c55e"
                strokeWidth="3.2"
                strokeDasharray={`${percentage(summary.compliant)} 100`}
                strokeDashoffset="0"
              />

              {/* Attention Section */}
              <circle
                cx="18"
                cy="18"
                r="15.915"
                fill="transparent"
                stroke="#f59e0b"
                strokeWidth="3.2"
                strokeDasharray={`${percentage(summary.attention)} 100`}
                strokeDashoffset={-percentage(summary.compliant)}
              />

              {/* Non-Compliant Section */}
              <circle
                cx="18"
                cy="18"
                r="15.915"
                fill="transparent"
                stroke="#ef4444"
                strokeWidth="3.2"
                strokeDasharray={`${percentage(summary.nonCompliant)} 100`}
                strokeDashoffset={-percentage(summary.compliant) - percentage(summary.attention)}
              />
            </svg>

            {/* Absolute Centered Internal Metrics Display */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="text-xl font-bold tracking-tight text-slate-900">
                {loading ? '…' : error || !summary.total ? '—' : percentage(summary.compliant) + '%'}
              </span>

              <span className="text-[10px] font-medium text-slate-400 tracking-wide mt-0.5">
                Compliant
              </span>
            </div>
          </div>

          {/* Graphical Key Labels & Metrics Panel Grid */}
          <div className="col-span-7 flex flex-col gap-3 pl-4">
            {/* Compliant Counter Item */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />

                <span className="font-semibold text-slate-600">Compliant</span>
              </div>

              <span className="font-bold text-slate-800">
                {value(summary.compliant)}{" "}
                <span className="font-medium text-slate-400 ml-0.5">({percentage(summary.compliant)}%)</span>
              </span>
            </div>

            {/* Attention Counter Item */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />

                <span className="font-semibold text-slate-600">Attention</span>
              </div>

              <span className="font-bold text-slate-800">
                {value(summary.attention)}{" "}
                <span className="font-medium text-slate-400 ml-0.5">
                  ({percentage(summary.attention)}%)
                </span>
              </span>
            </div>

            {/* Non-Compliant Counter Item */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />

                <span className="font-semibold text-slate-600">
                  Non Compliant
                </span>
              </div>

              <span className="font-bold text-slate-800">
                {value(summary.nonCompliant)}{" "}
                <span className="font-medium text-slate-400 ml-0.5">
                  ({percentage(summary.nonCompliant)}%)
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Lower Variance Comparison Trend Badge */}
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-50">
          <div className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-50 border border-green-100 rounded text-[11px] font-bold text-green-700">
            <span>—</span>
          </div>

          <span className="text-xs font-medium text-slate-400">
            Status history not tracked
          </span>
        </div>
      </div>

      {/* SECTION 2: Upcoming Maintenance Schedule List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {/* Container Context Header Toolbar Line */}
        <div className="flex items-center justify-between gap-4 p-5 pb-3 bg-white">
          <h3 className="text-base font-bold text-slate-900">
            Upcoming Maintenance
          </h3>

          <a
            href="#maintenance-calendar"
            onClick={event => { event.preventDefault(); setCalendarOpen(true); }}
            className="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline transition-all"
          >
            View Calendar
          </a>
        </div>

        {/* Core Linear Dynamic Maintenance Rows Grid Frame */}
        <div className="divide-y divide-slate-100 px-5">
          {!maintenanceItems.length && <p className="py-5 text-xs text-slate-400">{loading ? 'Loading maintenance…' : error ? 'Unable to load maintenance.' : 'No upcoming maintenance scheduled.'}</p>}
          {maintenanceItems.slice(0, 4).map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-4 py-3.5 hover:bg-slate-50/30 transition-colors"
            >
              {/* Identity & Room Block Grid */}
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-50 rounded-lg border border-slate-100 text-slate-500 shrink-0">
                  <FiTool className="w-4 h-4" />
                </div>

                <div className="flex flex-col">
                  <span className="text-xs font-bold text-slate-800 leading-tight">
                    {item.name}
                  </span>

                  <span className="text-[11px] text-slate-400 font-medium mt-0.5">
                    {item.location}
                  </span>
                </div>
              </div>

              {/* Exact Timeline Target Flag */}
              <div className="text-right flex flex-col items-end sm:flex-row sm:items-center sm:gap-8">
                <span className="text-xs font-semibold text-slate-600 font-mono tracking-tight">
                  {item.date}
                </span>

                <span
                  className={`text-xs ${item.colorClass} mt-0.5 sm:mt-0 min-w-[65px] text-right`}
                >
                  {item.due}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* View All Aggregator Action Navigation Link Footer */}
        <div className="p-4 bg-slate-50/50 border-t border-slate-100 px-5">
          <a
            href="#equipment-inventory"
            onClick={event => { event.preventDefault(); filterInventory("maintenance"); }}
            className="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline inline-block transition-all"
          >
            View all maintenance ({maintenanceItems.length})
          </a>
        </div>
      </div>
    </div>
  );
}
